import Alert from '@components/v2/ui/Alert/Alert';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Button } from '@mui/material';
import { Grid } from '@mui/material';
import { Plus } from 'lucide-react';
import * as R from 'ramda';
import { useState } from 'react';
import Dropzone from 'react-dropzone';
import { makeStyles } from 'tss-react/mui';
import { OnDragEndParams } from '../../components/DragAndDrop/dragAndDrop.types';
import PickImage from '../../components/FormInputs/PickMedia';
import useWidth from '../../components/Hooks/useWidth';
import When from '../../components/When';
import useMediaStyles, { imageAspectRatioPadding } from '../../components/useMediaStyles';
import LangEnum from '../../lang/LangEnum';
import { FileCoverUrlProcessor, IMedia, processCoverUrlDefault } from '../../lib/MediasLib';
import { useTranslation } from '../translation/TranslationContext';
import MediaListContainer from './MediaListContainer';
import { buildMaxSizeDescription } from './medias.utils';
import { stringMediaMimeType } from './mimeType';

const useStyles = makeStyles()((theme) => ({
  mobileButton: {
    '& div': {
      width: '100%',
    },
    '& svg': {
      position: 'absolute',
    },
    '& p': {
      width: '100%',
    },
  },
  dropzone: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  addImageContainer: {
    aspectRatio: `1 / ${Number.parseFloat(imageAspectRatioPadding) / 100}`,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: theme.palette.custom.border.primary,
    borderRadius: 4,
  },
  warningContainer: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.grey[200],
  },
  dropzoneContainer: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: theme.palette.custom.border.primary,
  },
  dragHover: {
    border: `2px dashed ${theme.palette.custom.border.primary}`,
  },
  mediaListContainer: {
    overflowX: 'auto',
    maxHeight: 'unset',
    [theme.breakpoints.only('xs')]: {
      maxHeight: '100vh',
    },
  },
  addIcon: {
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
      borderRadius: '100%',
    },
  },
}));

interface MediasOptions {
  showRemoveMediaButton: boolean;
  showAddMediaButton: boolean;
  showEditImage: boolean;
  inModal: boolean;
  mediaContentContain?: boolean;
  disableDrag?: boolean;
}

interface MediasManagerProps {
  medias: IMedia[];
  tag: 'NORMAL' | 'CHANGED' | 'LOADING' | 'SUCCESS' | 'ERROR';
  mimeTypes?: string;
  errorMessage?: string;
  onAdd: (addedItem: IMedia[]) => void;
  maxSize: number;
  onRemoveClick: (media: IMedia) => Promise<void>;
  processFileCover?: FileCoverUrlProcessor;
  onSortEnd?: (data: OnDragEndParams) => void;
  mainLegend?: string;
  options?: MediasOptions;
  id: string;
  label?: string;
  description?: string;
  mobileLabel?: string;
}

const MediasManager = ({
  mimeTypes,
  tag,
  errorMessage,
  medias,
  onAdd,
  maxSize = 10,
  mainLegend,
  onRemoveClick,
  onSortEnd,
  processFileCover,
  id,
  label = 'clique aqui ou arraste seus arquivos para carregar',
  mobileLabel = 'adicionar mídias',
  description,
  options = {
    showRemoveMediaButton: true,
    showAddMediaButton: true,
    showEditImage: true,
    inModal: false,
    disableDrag: false,
  },
}: MediasManagerProps) => {
  const { mobileWidth } = useWidth();
  const { classes, cx } = useStyles();
  const { classes: mediaClasses } = useMediaStyles({});
  const [drag, setDrag] = useState<boolean>(false);

  const { translate } = useTranslation();

  const addAndProcessWithLimit = async (files: File[]) => {
    if (medias.length >= maxSize || !Array.isArray(files)) {
      return;
    }
    const allowableFiles = files.slice(0, maxSize - medias.length);

    const [firstFile] = allowableFiles;

    if (firstFile instanceof File) {
      onAdd(await Promise.all((processFileCover || processCoverUrlDefault)(allowableFiles)));
    } else {
      onAdd([await (processFileCover || processCoverUrlDefault)(allowableFiles)[0]]);
    }
  };

  const prevent = (e: TouchEvent) => {
    e.preventDefault();
  };

  const handleSortEnd = (data: OnDragEndParams) => {
    if (onSortEnd) {
      onSortEnd(data);
    }
    document.removeEventListener('touchmove', prevent);
  };

  const maxFileSize = !R.isEmpty(medias) && medias.length >= maxSize;
  const internalMimeType: string = mimeTypes || stringMediaMimeType;
  const maxSizeAlert = (
    <Alert
      severity="info"
      message={description ?? LangEnum.SHOWROOM_MEDIA_FILES_MAX_SIZE}
      values={{ maxFiles: maxSize, filesMaxSize: buildMaxSizeDescription(mimeTypes) }}
    />
  );
  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{
          pb: '12px',
        }}
      >
        <When value={options?.showAddMediaButton} is>
          <Grid
            size={{
              xs: 12,
              sm: 3,
            }}
          >
            {mobileWidth ? (
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    size="large"
                    disabled={maxFileSize}
                    className={classes.mobileButton}
                  >
                    <PickImage
                      disabled={maxFileSize}
                      id={id}
                      onChange={addAndProcessWithLimit}
                      multiple
                      accept={internalMimeType}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Icon icon={Plus} size="lg" />
                        <Typography variant="body1" align="center">
                          {maxFileSize
                            ? translate(LangEnum.SHOWROOM_MEDIA_MAX_FILES_WARNING)
                            : mobileLabel}
                        </Typography>
                      </Box>
                    </PickImage>
                  </Button>
                </Grid>
                <Grid size={12}>{maxSizeAlert}</Grid>
              </Grid>
            ) : (
              <Dropzone
                accept={internalMimeType}
                onDropAccepted={addAndProcessWithLimit}
                noClick
                {...(!options?.disableDrag && {
                  onDrop: () => setDrag(false),
                  onDragEnter: () => setDrag(true),
                  onDragLeave: () => setDrag(false),
                })}
              >
                {({ getRootProps }) => (
                  <div
                    {...getRootProps()}
                    className={cx(
                      mediaClasses.root,
                      classes.addImageContainer,
                      maxFileSize ? classes.warningContainer : classes.dropzoneContainer,
                    )}
                  >
                    <div className={cx(classes.dropzone, drag ? classes.dragHover : '')}>
                      <PickImage
                        disabled={maxFileSize}
                        id={id}
                        onChange={addAndProcessWithLimit}
                        multiple
                        accept={internalMimeType}
                      >
                        {!maxFileSize && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              className={classes.addIcon}
                              sx={{
                                width: 48,
                                height: 48,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.primary',
                              }}
                            >
                              <Icon icon={Plus} size="lg" />
                            </Box>
                          </Box>
                        )}
                        <Typography variant="h6" color="text.primary">
                          {maxFileSize
                            ? translate(LangEnum.SHOWROOM_MEDIA_MAX_FILES_WARNING)
                            : label}
                        </Typography>
                      </PickImage>
                    </div>
                  </div>
                )}
              </Dropzone>
            )}
          </Grid>
        </When>
        <Grid
          className={classes.mediaListContainer}
          size={{
            xs: 12,
            sm: 9,
          }}
        >
          <MediaListContainer
            onRemoveClick={onRemoveClick}
            list={medias}
            options={options}
            {...(!options?.disableDrag && {
              useDragHandle: true,
              mainLegend,
              onSortStart: () =>
                document.addEventListener('touchmove', prevent, { passive: false }),
            })}
            onSortEnd={handleSortEnd}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        sx={{
          justifyContent: mobileWidth ? 'center' : 'flex-end',
        }}
      >
        {!mobileWidth && <Grid size={12}>{maxSizeAlert}</Grid>}
        <When value={tag} is="ERROR">
          <Grid>
            <Typography variant="body1" color="error.main">
              {translate(LangEnum.SNACKBAR_ERROR_TRY_AGAIN)}: {errorMessage}
            </Typography>
          </Grid>
        </When>
      </Grid>
    </>
  );
};

export default MediasManager;
